import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView } from 'react-native'
import { Button, Text, Spinner, Toggle, Radio, Datepicker, CheckBox } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import MoneyTextInput from '../../components/MoneyTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveRecurringSpendings, deleteRecurringSpending } from '../../global/Firebase/firebase'
import { rethrowUnlessWRefusal } from '../../global/Write/writeW'
import HeaderIconButton from '../../components/HeaderIconButton'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { localeDateServiceAtom, storesDictAtom } from '../../global/AtomsProvider'
import { useAtomValue } from 'jotai'
import { normalizeDate } from '../../utils/timezoneFunctions'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { parseDecimalAmount } from '../../utils/decimalInput'

// Extract only primitive fields from store object (avoid functions/refs that can't be serialized)
const getStoreFields = (storeObj) => {
  if (!storeObj) return null
  return { id: storeObj.id, name: storeObj.name, supabaseId: storeObj.supabaseId }
}

export default function RecurringSpendingDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const { detail, detailIndex, allData } = route.params
  const storesDict = useAtomValue(storesDictAtom)
  const localeDateService = useAtomValue(localeDateServiceAtom)
  // ATL-810 — a NEW recurring spending must be born with an id, exactly like every other constants
  // detail screen (`wallet-`, `paymentMethod-`, `purchaseMethod-`, `productUnit-`, `productModifier-`).
  // This was the only one without a generator, so an item created in the app reached Firestore with no
  // `id` at all. `migrateConstants` then keys its Supabase row by CONTENT HASH
  // (`recurringSpendings#<sha1>`, ATL-799), which the client cannot reproduce — so
  // `saveRecurringSpendings` resolves `info.id || info.firestoreId || info.supabaseId` to undefined and
  // SKIPS the update, and `deleteRecurringSpending` sends undefined and 404s. Write-only rows: an edit
  // never leaves the device and a deleted rent line survives in Supabase. Measured 2026-08-02: 298 of
  // 306 live rows across 201 orgs are hash-keyed. These are scheduled money movements.
  //
  // The `detail ? … : …` ternary is LOAD-BEARING and is why this change is safe by construction — it
  // must NOT be `detail?.id || generated`:
  //   - NEW item (`detail === null`, `detailIndex === allData.length`) → generated id. No Supabase row
  //     exists yet, so nothing is re-keyed; the create dual-write carries the id as `firestoreId` and
  //     both stores agree from birth.
  //   - EXISTING item → `detail.id` verbatim, including `undefined`. Minting an id here would be the
  //     ATL-799 defect: the Supabase row is stored under its content hash, so the next migration would
  //     take the id-bearing branch of `resolveConstantFirestoreIds`, INSERT a second row, and duplicate
  //     the spend. Id-less legacy items therefore behave exactly as they do today (still write-only)
  //     until the one-shot backfill runs before the Phase-5 read cutover — deliberately not this change.
  //
  // `recurringSpending-<ms>` cannot collide with the signup seed's literal `recurringSpending1`
  // (ATL-802): the hyphen is not a digit, so the two id spaces are disjoint.
  const [id] = useState(detail ? detail.id : ('recurringSpending-' + Date.now()))
  const [name, setName] = useState(detail ? detail.name : '')
  const [store, setStore] = useState(getStoreFields(detail?.store || storesDict?.[context.user.store.id]))
  const [date, setDate] = useState(normalizeDate(detail?.date) || new Date())
  const [endDate, setEndDate] = useState(normalizeDate(detail?.endDate))
  const [endDateVisible, setEndDateVisible] = useState((detail && detail.endDate) ? true : false)
  const [amount, setAmount] = useState(detail ? detail.amount.toString() : '')
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const _datePicker = React.createRef()
  const _endDatePicker = React.createRef()

  // Emit detail data for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[RECURRING_SPENDING_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
      })));
    }
  }, [detail]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <HeaderIconButton
          onPress={() => {
            deleteRecurringSpending(context.db, detailIndex, allData).catch(rethrowUnlessWRefusal) // P9b review 1: a W refusal was already announced
            navigation.goBack()
          }}
          iconName='trash-outline'
        />
      ),
      title: (detail?.name || t('spendings.newMonthlySpending'))
    })
  }, [])

  async function save() {
    setIsLoading(true)
    // ATL-1371 — this is a SCHEDULED money movement, and it was stored through `parseInt`: a rent
    // line of 36.5 was written as 36 every month, and '36,5' from a ru/kk keypad the same way.
    // A null here means the box is empty or unreadable, which is the `notAllFieldsFilled` case
    // this screen already had.
    const numberAmount = parseDecimalAmount(amount)
    if (name !== '' && numberAmount !== null) {
      setSaveError('')
      let info = {
        // Preserve IDs from detail for updates (needed for Supabase API); for a NEW item this is the
        // id minted once at mount (ATL-810), so the row is addressable from the very first write.
        id,
        firestoreId: detail?.firestoreId,
        supabaseId: detail?.supabaseId,
        // User-editable fields
        name,
        amount: numberAmount,
        type: 'monthly',
        date,
        endDate: endDateVisible ? endDate : null,
        store,
        organizationId: context.user.organizationId
      }
      try {
        await saveRecurringSpendings(context.db, info, detailIndex, allData)
        setSuccessMessageVisible(true)
        setIsLoading(false)
      } catch (error) {
        Sentry.captureException(error)
        console.log(error)
        setSaveError(t('error'))
        setIsLoading(false)
      }
    } else {
      setSaveError(t('notAllFieldsFilled'))
      setIsLoading(false)
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
    
        <Text category='label' style={styles.label}>
          {t('spendings.name')}
        </Text>
        <StyledTextInput 
          value={name}
          onChangeText={text => setName(text)}
          returnKeyType='done'
          autoCapitalize='sentences'
        />
        
        <Text category='label' style={styles.label}>
          {t('spendings.amount')}
        </Text>
        <MoneyTextInput
          value={amount}
          onChangeText={text => setAmount(text)}
          returnKeyType='done'
        />

        <Text category='label' style={styles.label}>{t('store')}</Text>
        { Object.values(storesDict).map((item, index) => (
          <Radio key={item.id} style={styles.radio} checked={store?.id === item.id} onChange={() => {
            setStore(getStoreFields(Object.values(storesDict)[index]))
          }}>
            {item.name}
          </Radio>
        ))}

        <Text category='label' style={styles.label}>
          {t('spendings.startDate')}
        </Text>
        <Datepicker
          placement='top'
          date={date}
          dateService={localeDateService}
          onSelect={(date) => {
            setDate(date)
            _datePicker.current.blur()
          }}
          min={new Date('2020-12-01')}
          max={new Date('2029-12-31')}
          ref={_datePicker}
        />

        <CheckBox style={{marginTop: 10}} checked={endDateVisible} onChange={(value) => {
          setEndDateVisible(value)
        }}>
          {t('spendings.hasEndDate')}
        </CheckBox>

        { endDateVisible && (
          <>
            <Text category='label' style={{marginTop: 5, marginBottom: 5}}>
              {t('spendings.endDate')}
            </Text>
            <Datepicker
              placement='top'
              date={endDate}
              dateService={localeDateService}
              onSelect={(date) => {
                setEndDate(date)
                _endDatePicker.current.blur()
              }}
              min={date}
              max={new Date('2029-12-31')}
              ref={_endDatePicker}
            />    
          </>
        )}

        <Text category='label' style={styles.label}>
          {t('spendings.typeRecurring')}
        </Text>
        
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton}>
            {t('save')}
          </Button>
          )
        )}

      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  radio: {
    marginBottom: 5
  },
});